
import { useState, useEffect } from 'react';
import { useSupabaseClient } from './useSupabaseClient';
import { useToast } from './use-toast';
import { Procurement } from '@/types/procurement';
import { AnalysisWithProcurement } from '@/types/analysis';
import { 
  fetchCompletedAnalyses,
  fetchInProgressAnalyses, 
  fetchAnalysisProcurementIds,
  createAnalysis,
  updateAnalysisProgress
} from '@/services/analysisService';
import { fetchPendingProcurements } from '@/services/procurementService';

export const useAnalyses = () => {
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  const [completedAnalyses, setCompletedAnalyses] = useState<AnalysisWithProcurement[]>([]);
  const [inProgressAnalyses, setInProgressAnalyses] = useState<AnalysisWithProcurement[]>([]);
  const [pendingAnalyses, setPendingAnalyses] = useState<Procurement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);

      // Fetch completed analyses
      const completed = await fetchCompletedAnalyses(supabase);
      
      // Fetch in-progress analyses
      const inProgress = await fetchInProgressAnalyses(supabase);
      
      // Get all procurement IDs that have analyses
      const analysedIds = await fetchAnalysisProcurementIds(supabase);
      
      // Fetch procurements without analyses (pending)
      const pending = await fetchPendingProcurements(supabase, analysedIds);

      setCompletedAnalyses(completed);
      setInProgressAnalyses(inProgress);
      setPendingAnalyses(pending);
    } catch (error: any) {
      console.error('Error fetching analyses:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte hämta analyser: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startAnalysis = async (procurementId: string) => {
    try {
      await createAnalysis(supabase, procurementId);
      
      toast({
        title: "Analys startad",
        description: "Analysen har påbörjats och kommer att uppdateras kontinuerligt.",
      });
      
      // Refresh the analyses data
      await fetchAnalyses();
    } catch (error: any) {
      console.error('Error starting analysis:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte starta analysen: " + error.message,
        variant: "destructive",
      });
    }
  };

  const updateProgress = async (analysisId: string, currentProgress: number | null) => {
    try {
      const updateData = await updateAnalysisProgress(supabase, analysisId, currentProgress);
      
      toast({
        title: updateData.status === 'Klar' ? "Analys slutförd!" : "Analys uppdaterad",
        description: updateData.status === 'Klar' 
          ? "Analysen är nu klar och resultaten finns tillgängliga."
          : `Analysen fortskrider, ${updateData.progress}% färdig.`,
      });
      
      // Refresh the analyses data
      await fetchAnalyses();
    } catch (error: any) {
      console.error('Error updating analysis:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte uppdatera analysen: " + error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchAnalyses();
  }, [supabase, toast]);

  return {
    completedAnalyses,
    inProgressAnalyses,
    pendingAnalyses,
    loading,
    startAnalysis,
    updateProgress,
    refreshAnalyses: fetchAnalyses
  };
};
